// eslint-disable-next-line no-redeclare, @typescript-eslint/no-redeclare
import { Button, Container, Group, List, Stack, Text as MantineText } from '@mantine/core';
import { IconArrowRight, IconCheck, IconUsers } from '@tabler/icons';
import { useContext, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import BookitupAlert from '../../../Atoms/BookitupAlert';
import { useModalState } from '../../../Molecules/Dialogs';
import { useAddon, useBookitupSubscription } from '../../../Service/Context/BookitupSubscriptionContext';
import { ProfileContext } from '../../../Service/Context/ProfileContext';
import { ViolationContext } from '../../../Service/Context/ViolationContext';
import { LicenceS } from '../../../Service/LicenceS';
import { RoutingS } from '../../../Service/RoutingS';
import LinkText from '../../../Atoms/LinkText';
import { ThemeText, ThemeTitle } from '../../../Atoms/ThemeText';
import PagePermissionGuard from '../../Security/PagePermissionGuard';
import AddTeamMemberModal from './AddTeamMemberModal';
import { openTeamAddonMissingModal, openTeamAddonQuantityModal } from './bookitupTeamAddonModals';
import TeamMembers from './TeamMembers';

const BookitupTeam = () => {
  const { executeWithLicenceCheck } = useContext(ViolationContext);
  const { bookitupTeam, isOwnProfile: isOwner } = useContext(ProfileContext);
  const { show, toggle } = useModalState(false);
  const { isPresent, addon } = useAddon('team');
  const { subscription, permissionsLoaded } = useBookitupSubscription();
  const navigate = useNavigate();

  useEffect(() => {
    RoutingS.changeTitle('Team');
  }, []);

  const openAddTeamMemberModal = async () => {
    if (!isPresent) {
      openTeamAddonMissingModal(navigate, subscription?.interval);
      return;
    }
    const shares = bookitupTeam.sharesLoaded
      ? bookitupTeam.shares
      : await bookitupTeam.ensureSharesLoaded().catch(() => bookitupTeam.shares);

    if (addon && addon.quantity <= shares.length) {
      openTeamAddonQuantityModal(navigate, addon.quantity, shares.length);
      return;
    }
    executeWithLicenceCheck(LicenceS.Restrictions.SHARE_PROFILE, toggle);
  };

  const openTeamAddonCheckout = () => {
    const billingInterval = subscription?.interval ?? 'Yearly';
    const searchParams = new URLSearchParams({
      kind: 'addon',
      itemId: `TEAM-EUR-${billingInterval}`,
      quantity: '1',
    });

    navigate(`${RoutingS.ROUTES.PLANS}?${searchParams.toString()}`);
  };

  return (
    <PagePermissionGuard ownerOnly>
      <Container p={0} size={850}>
        <Stack spacing="xl">
          <Group position="apart" align="flex-start" noWrap>
            <div>
              <ThemeTitle order={3}>Team &amp; Zugänge</ThemeTitle>
              <ThemeText mt="md">
                Lade Dein Team ein, gemeinsam in bookitup zu arbeiten. Vergib passende Zugänge und entscheide, welche
                Module, Daten und E-Mail-Funktionen jede Person nutzen darf.
              </ThemeText>
            </div>
            {isOwner && (
              <div style={{ whiteSpace: 'nowrap' }}>
                <LinkText onClick={openAddTeamMemberModal} size="sm">
                  + Hinzufügen
                </LinkText>
              </div>
            )}
          </Group>
          {permissionsLoaded && subscription && !isPresent && (
            <BookitupAlert
              icon={<IconUsers size={22} />}
              title="Gemeinsam mehr schaffen – mit dem Team Add-on"
              radius="md"
              sx={(theme) => ({
                border: `1px solid ${theme.colorScheme === 'dark' ? theme.colors.blue[7] : theme.colors.blue[1]}`,
                background:
                  theme.colorScheme === 'dark'
                    ? `linear-gradient(135deg, ${theme.colors.dark[6]} 0%, ${theme.colors.blue[9]} 100%)`
                    : `linear-gradient(135deg, ${theme.colors.lightBlue[0]} 0%, ${theme.white} 100%)`,
              })}
            >
              <MantineText size="sm">
                Hole Dein Team in bookitup und organisiere Events, Aufgaben und Kunden gemeinsam an einem Ort.
              </MantineText>
              <List mt="sm" spacing={5} size="xs" icon={<IconCheck size={13} stroke={3} color="#FDAF47" />}>
                <List.Item>Eigene Zugänge für Deine Teammitglieder</List.Item>
                <List.Item>Aufgaben und Events gezielt zuweisen</List.Item>
                <List.Item>Individuelle Rechte für sensible Daten und Funktionen</List.Item>
              </List>
              <Button size="xs" mt="md" rightIcon={<IconArrowRight size={14} />} onClick={openTeamAddonCheckout}>
                Team Add-on entdecken
              </Button>
            </BookitupAlert>
          )}
          <TeamMembers />
        </Stack>
        <AddTeamMemberModal show={show} toggle={toggle} />
      </Container>
    </PagePermissionGuard>
  );
};

export default BookitupTeam;
